import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
  type PropsWithChildren,
} from "react";
import type { SettingIndex } from "../../../api/useSettings";
import { useSettings } from "../../../api/useSettings";
import Text from "@ui/Text";
import t from "../../../i18n";
import { color } from "@ui";
import Switch from "@ui/Switch";
import { usePut } from "../../../api/useCall";
import TranslationTable from "./translation";
import { useCanUserDo } from "../../canUserDo";
import { spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import Select, { type SelectOption } from "@ui/Select";
import { LiveDataWorkflowAPIKey } from "./LiveDataWorkflow";
import { Legals } from "./Legals";
import Button from "@ui/Button";
import Modal from "@ui/Modal";
import useModal from "@ui/lib/hooks/useModal";
import ApplicationLinkTable from "./ApplicationLink/ApplicationLinkTable";
import { ModalFormUpsertLink } from "./ApplicationLink/ModalFormUpsertLink";
import { type AggregationPeriod } from "../../../domain/aggregation/period";
import { DevelopmentBadge } from "@ui/Badge/Development";
import { ApplicationAnchors, applicationSettingNavigation } from "./ApplicationAnchors";
import { FreshDataForm } from "./Forms/FreshDataForm";
import { ManualFaultMigrationDryRun } from "./ManualFaultMigrationDryRun";
import { ManualFaultMigration } from "./ManualFaultMigration";
import ManualFaultMigrationRollback from "./ManualFaultMigrationRollback";
import AlgorithmVersions from "./AlgorithmVersions";
import PowerOutputCacheAction from "./PowerOutputCacheAction";
import ErrorBoundary from "../../ErrorBoundary";
import LoadingBoundary from "@ui/LoadingBoundary";

const aggregationPeriodOptions: SelectOption<
  Extract<AggregationPeriod, "CYCLE" | "CYCLE_2" | "P1M" | "P3M" | "P6M">
>[] = [
  { value: "CYCLE", label: t("period.selector.last_group") },
  { value: "CYCLE_2", label: t("period.selector.last_two_groups") },
  { value: "P1M", label: t("period.selector.1m.long") },
  { value: "P3M", label: t("period.selector.3m.long") },
  { value: "P6M", label: t("period.selector.6m.long") },
];

export function Application() {
  const settings = useSettings();
  const canEdit = useCanUserDo("ADMIN_SETTING_MANAGE");
  const updateSetting = usePut("/settings");

  const { modal: linkModal, openModal: openLinkModal, closeModal: closeLinkModal } = useModal();

  const aggregationPeriodValue = useMemo(
    () => settings.AGGREGATION_PERIOD,
    [settings.AGGREGATION_PERIOD],
  );

  const handleSettingAlgorithmVersion = React.useCallback(
    async (value: string) => {
      if (settings["DEFAULT_DIAGNOSIS_PRESET"] === value) return;

      await updateSetting({
        body: {
          DEFAULT_DIAGNOSIS_PRESET: value,
        },
      });
    },
    [settings, updateSetting],
  );

  const handleSettingAggregationPeriod = React.useCallback(
    async (value: string) => {
      if (settings["AGGREGATION_PERIOD"] === value) return;

      await updateSetting({
        body: {
          AGGREGATION_PERIOD: value,
        },
      });
    },
    [settings, updateSetting],
  );

  const [_, setAnchor] = useState<string | null>(window.location.hash.slice(1));
  useEffect(() => {
    setTimeout(() =>
      document
        .getElementById(window.location.hash.slice(1))
        ?.scrollIntoView({ behavior: "smooth" }),
    );
    window.addEventListener("hashchange", () => setAnchor(window.location.hash.slice(1)));
  }, []);

  return (
    <main className={applicationSettings}>
      <Text type="h2" as="h1" className={applicationSettingTitle}>
        {t("customization.title")}
      </Text>

      <section id="data-import" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.data_import.title")}
        </Text>
        {canEdit && (
          <>
            <FeatureFlag
              flag="SOURCE_SNAPSHOT_IMPORT_IN_PROJECT"
              hint={t("customization.section.data_import.tooltips.active_data_import.text")}
            >
              {t("customization.section.data_import.switch.active_data_import.text")}
            </FeatureFlag>
            <FeatureFlag
              flag="SOURCE_SNAPSHOT_IMPORT_CONTEXT_FILE"
              hint={t("customization.section.data_import.tooltips.source_import_conflict.text")}
            >
              {t("customization.section.data_import.switch.active_context_file_format.text")}
            </FeatureFlag>
            <FeatureFlag
              flag="ALLOW_SNAPSHOT_UPDATES_ON_CONFLICT"
              hint={t("customization.section.data_import.tooltips.source_import_conflict.text")}
            >
              {t("customization.section.data_import.switch.source_import_conflict.text")}
            </FeatureFlag>
          </>
        )}
      </section>

      <section id="live-data-workflow" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("administration.application.api_key.title")}
        </Text>
        <LiveDataWorkflowAPIKey />
      </section>

      <section id="data-display" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.data_display.title")}
        </Text>
        {canEdit && (
          <>
            <FeatureFlag
              flag="PROJECT_TYPE_QUANTITY_GROUP"
              hint={t(
                "customization.section.data_display.tooltips.group_quantities_by_system.text",
              )}
            >
              {t("customization.section.data_display.switch.group_quantities_by_system.text")}
            </FeatureFlag>
            <FeatureFlag
              flag="PROJECT_HIDDEN_GLOBAL_IMPACT"
              hint={t(
                "customization.section.data_display.tooltips.hide_symptoms_global_impact.text",
              )}
            >
              {t("customization.section.data_display.switch.hide_symptoms_global_impact.text")}
            </FeatureFlag>
            <FeatureFlag
              flag="DISPLAY_PUBLICATION_MARKERS"
              hint={t(
                "customization.section.data_display.tooltips.display_publication_markers.text",
              )}
            >
              {t("customization.section.data_display.switch.display_publication_markers.text")}
            </FeatureFlag>
            <Select
              prompt="aggregation_period"
              label={t("app.customization.aggregation_period.select.label")}
              tooltipOverlay={t("app.customization.aggregation_period.select.tooltip")}
              value={aggregationPeriodValue}
              options={aggregationPeriodOptions}
              onChange={e => handleSettingAggregationPeriod(e.target.value)}
              clearable={false}
            />
            <PowerOutputCacheAction />
          </>
        )}
      </section>

      <section id="fault-analysis" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.failure_analysis.title")}
        </Text>
        {canEdit && (
          <FeatureFlag
            flag="AUTOMATIC_DIAGNOSIS_RELAUNCH"
            hint={t(
              "customization.section.failure_analysis.tooltips.restart_diagnostics_manually.text",
            )}
          >
            {t("customization.section.failure_analysis.switch.restart_diagnostics_manually.text")}
          </FeatureFlag>
        )}
      </section>
      <section id="fault-migration" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.failure_analysis.migration.title")}
        </Text>
        <ManualFaultMigrationDryRun canEdit={canEdit} />
        <ManualFaultMigration canEdit={canEdit} />
        <ManualFaultMigrationRollback canEdit={canEdit} />
      </section>
      <section id="digital-twin" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.digital_twin.title")}
        </Text>
        {canEdit && (
          <FeatureFlag
            flag="ALLOW_USE_DYMOSIM_AND_FMU_T0"
            hint={t("customization.section.failure_analysis.tooltips.allow_dymosim_fmu_t0.text")}
          >
            {t("customization.section.failure_analysis.switch.allow_dymosim_fmu_t0.text")}
          </FeatureFlag>
        )}
      </section>

      <section id="algorithm" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("setup.diagnosis.algorithm.title")}
        </Text>
        <ErrorBoundary>
          <LoadingBoundary>
            <AlgorithmVersions
              currentAlgorithmSetting={settings.DEFAULT_DIAGNOSIS_PRESET}
              handleSettingAlgorithmVersion={handleSettingAlgorithmVersion}
            />
          </LoadingBoundary>
        </ErrorBoundary>
      </section>

      <section id="refresh-sources" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.demo_environment.keep_demos_updated")}
        </Text>
        <Text>{t("customization.demo_environment.keep_demos_updated.tooltip")}</Text>
        <FreshDataForm originalSettingsValue={settings.FRESHLY_MAINTAINED_SOURCES} />
      </section>

      <section id="advanced-features" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.advanced_features.title")}
        </Text>
        <FeatureFlag flag="DISPLAY_FINANCIAL_IMPACT_DASHBOARD">
          {t("customization.section.data_display.switch.display_financial_impact_dashboard.text")}
        </FeatureFlag>
        <FeatureFlag flag="FLEET_VIEW_ENABLED">
          {t("customization.section.fleet_view.switch")}
        </FeatureFlag>
        <FeatureFlag flag="FAULT_EVENTS_ENABLED">
          {t("customization.section.events.switch")}
        </FeatureFlag>
        <FeatureFlag flag="FAULT_EVENTS_ADMIN_ENABLED">
          {t("customization.section.events.admin.switch")}
        </FeatureFlag>
        <FeatureFlag flag="FAULT_EVENTS_GROUPING_ENABLED">
          {t("customization.section.events.grouping")}
          <DevelopmentBadge />
        </FeatureFlag>
        <FeatureFlag flag="FAULT_EVENTS_DISABLE_MEDIUM_PRIORITY">
          {t("customization.section.events.priority_toggle")}
        </FeatureFlag>
        <FeatureFlag flag="FAULT_EVENTS_DISABLE_EVENT_GROUP_CONSTRAINT">
          {t("customization.section.events.disable_event_group_constraint")}
        </FeatureFlag>
        <FeatureFlag flag="FAILURE_GROUPING_ENABLED">
          {t("customization.section.failure_grouping.switch")}
        </FeatureFlag>
        <FeatureFlag flag="QUANTITY_VERSIONING_ENABLED">
          {t("customization.section.quantity.versioning.name")}
          <DevelopmentBadge />
        </FeatureFlag>
        <FeatureFlag flag="FLEET_VIEW_REPAIRABILITY_MATRIX">
          {t("customization.section.fleet_view.repairability_matrix")}
        </FeatureFlag>
        <FeatureFlag flag="MANUAL_EVENTS_DEV_MODE_ENABLED">
          {t("customization.section.event.enable_manual_events_dev_mode")}
          <DevelopmentBadge />
        </FeatureFlag>
        <FeatureFlag flag="SETUP_MODE_FEATURE_ENABLED">
          {t("customization.section.setup_mode")}
        </FeatureFlag>
        <FeatureFlag flag="NEW_NAVIGATION_ENABLED">
          {t("customization.section.quantities.new_navigation")}
        </FeatureFlag>
        <FeatureFlag flag="DB_PRISMA_REPLACE_ENABLED">
          {t("customization.section.ab.db_prisma_replace_enabled")}
          <DevelopmentBadge />
        </FeatureFlag>
        <FeatureFlag flag="HIDE_LEGACY_SUMMARY_PAGE">
          {t("customization.section.hide_legacy_summary_page")}
        </FeatureFlag>
        <FeatureFlag flag="SHOW_LEGACY_SUMMARY_BADGE">
          {t("customization.section.show_legacy_summary_badge")}
        </FeatureFlag>
        <FeatureFlag flag="FORMULA_VERSIONS_ENABLED">
          {t("customization.section.formula_versions_enabled")}
          <DevelopmentBadge />
        </FeatureFlag>
      </section>

      <section id="terminology" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.terminology.title")}
        </Text>
        <TranslationTable />
      </section>

      <section id="link" className={applicationSettingSectionLink}>
        <div className={linkTitle}>
          <Text type="h4" as="h2" className={settingSectionTitle}>
            {t("customization.section.link.title")}
          </Text>
          <Button variant="primary" onClick={() => openLinkModal()}>
            {t("customization.section.link.title.button.add")}
          </Button>
        </div>
        <ApplicationLinkTable />
        <Modal
          isOpen={linkModal}
          title={t("customization.application.link.modal.title")}
          subtitle={t("customization.application.link.modal.subtitle")}
          onRequestClose={closeLinkModal}
        >
          <ModalFormUpsertLink onRequestClose={closeLinkModal} />
        </Modal>
      </section>

      <section id="legals" className={applicationSettingSection}>
        <Text type="h4" as="h2" className={settingSectionTitle}>
          {t("customization.section.legals.title")}
        </Text>
        <Legals path={settings.LEGAL_FILE_PATH} />
      </section>

      <ApplicationAnchors />
    </main>
  );
}

const FeatureFlag = ({
  flag,
  hint,
  children,
}: PropsWithChildren<{
  flag: keyof SettingIndex;
  hint?: ComponentProps<typeof Switch>["tooltipOverlay"];
}>) => {
  const settings = useSettings();

  if (typeof settings[flag] !== "boolean")
    throw new Error("FeatureFlag expects boolean settings only.");

  const updateSetting = usePut("/settings");
  const updateFeatureFlag = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      void updateSetting({
        body: {
          [flag]: e.target.checked,
        },
      });
    },
    [flag, updateSetting],
  );

  return (
    <Switch
      className={featureSwitch}
      checked={settings[flag]}
      onChange={updateFeatureFlag}
      tooltipOverlay={hint}
    >
      <span className={featureDisplay}>{children}</span>
    </Switch>
  );
};

//#region CSS
const featureSwitch = css`
  display: flex;
  align-items: center;
  a {
    margin-top: 0;
  }
`;

const applicationSettingSection = css`
  > * + * {
    margin-top: ${spacings.xs};
  }
`;
const applicationSettingSectionLink = css`
  > * + * {
    margin-top: ${spacings.m};
  }
`;

const applicationSettings = css`
  padding: ${spacings.xxl};
  display: grid;
  grid-template-columns: 1fr min-content;
  grid-auto-rows: auto;
  gap: ${spacings.xl};

  @media (max-width: 1024px) {
    column-gap: 0;
  }

  > *:not(.${applicationSettingNavigation}) {
    grid-column: 1 / span 1;
  }

  .${applicationSettingNavigation} {
    grid-column: 2 / span 1;
    grid-row: 1 / span 4;
  }
`;

const applicationSettingTitle = css`
  margin-bottom: ${spacings.m};
`;

const settingSectionTitle = css`
  color: ${color.carbon700};
  width: fit-content;
`;

const featureDisplay = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;

const linkTitle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
`;
//#endregion CSS
